import React from "react";
import { useNavigate } from "react-router-dom";
import { getStatusColor } from "../../services/contractService";

const urgencyColor = (days) => {
  if (days <= 7)  return "#a4262c";
  if (days <= 14) return "#ca5010";
  if (days <= 30) return "#c9a84c";
  return "#0078d4";
};

const urgencyLabel = (days) => {
  if (days <= 7)  return "Critical";
  if (days <= 14) return "Urgent";
  if (days <= 30) return "Soon";
  return "Upcoming";
};

export const ExpiryAlertsModal = ({ contracts, onClose }) => {
  const navigate = useNavigate();

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[10000]">
      <div className="bg-white border border-gray-200 rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl relative overflow-hidden">
        {/* Gold top bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-600 via-orange-600 to-blue-600"/>

        {/* Header */}
        <div className="px-7 py-6 border-b border-gray-200">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2.5 mb-1">
                <span className="text-xl">⚠️</span>
                <h2 className="font-serif text-xl md:text-2xl font-medium text-gray-900 m-0">
                  Renewable Contract Expiry Alerts
                </h2>
              </div>
              <p className="text-sm text-gray-700 m-0">
                {contracts.length} renewable contract{contracts.length!==1?"s":""} expiring within 60 days
              </p>
            </div>
            <button onClick={onClose} className="bg-transparent border-none text-gray-700 cursor-pointer text-2xl p-0 leading-none shrink-0 hover:text-gray-700">×</button>
          </div>
        </div>

        {/* Contract list */}
        <div className="overflow-y-auto flex-1 px-7 py-3">
          {contracts.length === 0 ? (
            <div className="text-center py-8 text-gray-700 text-sm">No contracts match this filter.</div>
          ) : (
            <div className="flex flex-col gap-2 mt-2">
              {contracts.map(c => {
                const uc = urgencyColor(c.daysLeft);
                const ucClass = c.daysLeft <= 7 ? "bg-red-50 border-red-300 text-red-700" :
                                c.daysLeft <= 14 ? "bg-orange-50 border-orange-300 text-orange-700" :
                                c.daysLeft <= 30 ? "bg-blue-900/20 border-blue-700/30 text-blue-400" :
                                "bg-blue-50 border-blue-300 text-blue-700";
                const urgencyClass = c.daysLeft <= 7 ? "bg-red-50 text-red-700" :
                                     c.daysLeft <= 14 ? "bg-orange-50 text-orange-700" :
                                     c.daysLeft <= 30 ? "bg-blue-900/25 text-blue-400" :
                                     "bg-blue-50 text-blue-700";

                return (
                  <div key={c.id}
                    onClick={()=>{ navigate("/contracts/"+c.id); onClose(); }}
                    className="flex items-center gap-3.5 p-3.5 rounded-xl cursor-pointer bg-gray-100/30 border border-gray-200 hover:bg-gray-100/50 transition-all group">

                    {/* Urgency strip */}
                    <div style={{backgroundColor: uc}} className="w-1 h-auto flex-shrink-0 rounded-sm" style={{minHeight: '40px'}}/>

                    {/* Days badge */}
                    <div className={`w-14 text-center flex-shrink-0 py-1 rounded-lg border ${ucClass}`}>
                      <div className="text-base font-bold font-mono leading-tight">{c.daysLeft}</div>
                      <div className="text-xs tracking-wider mt-0.5">DAYS</div>
                    </div>

                    {/* Contract info */}
                    <div className="flex-1 overflow-hidden">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-semibold text-gray-900 overflow-hidden text-ellipsis whitespace-nowrap">{c.Title||"Untitled"}</span>
                        {c.IsRenewal==="Yes" && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-900/25 text-blue-400 border border-blue-700/30 flex-shrink-0">RENEWABLE</span>
                        )}
                      </div>
                      <div className="flex gap-2.5 text-sm text-gray-700 font-mono flex-wrap">
                        <span className="font-bold text-gray-900">{c.RefNo}</span>
                        {c.VendorName && <span>{c.VendorName}</span>}
                        {c.ExpiryDate && <span>Expires {new Date(c.ExpiryDate).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</span>}
                      </div>
                    </div>

                    {/* Right side */}
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${urgencyClass}`}>{urgencyLabel(c.daysLeft)}</span>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{c.Status}</span>
                    </div>

                    <span className="text-gray-700 text-sm flex-shrink-0">→</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-7 py-3.5 border-t border-gray-200 flex justify-between items-center gap-4 flex-wrap">
          <span className="text-sm text-gray-700">Click any contract to review. You can also find these in the Contracts Registry.</span>
          <button onClick={onClose}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 border-none rounded-lg text-white cursor-pointer font-semibold text-sm transition-colors flex-shrink-0">
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};
